/**
 * /help — full-page AI Navigator chat.
 *
 * Replaces the old right-drawer HelpFab. Same POST /api/ai/navigator
 * backend, same tier gating (Studio/Pro/Trial/Grandfathered — Free
 * users see an upgrade nudge). The layout mirrors a chat surface:
 * scrolling message list on top, suggestion chips shown when empty,
 * composer pinned at the bottom.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { HelpCircle, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "How do I mark a delivery as done?",
  "Where do I add a new vendor?",
  "How do I invite a teammate?",
  "How do I record a payment?",
  "What's the trash retention window?",
];

export default function Help() {
  const { studio } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]); // { role, text, actions? }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const scrollRef = useRef(null);

  const plan = (studio?.plan || "free").toLowerCase();
  const enabled = plan !== "free";

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      try {
        const r = await api.get("/ai/quota");
        setQuota(r.data);
      } catch (_) { /* non-critical */ }
    })();
  }, [enabled]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (overrideText) => {
    const q = (overrideText ?? input).trim();
    if (!q || loading) return;
    if (!overrideText) setInput("");
    const history = messages.slice(-6).map((m) => ({ role: m.role, content: m.text }));
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/navigator", {
        query: q, conversation_history: history,
      });
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: data.answer || "",
        actions: data.suggested_actions || [],
      }]);
      // Refresh quota after a successful call
      try {
        const r = await api.get("/ai/quota");
        setQuota(r.data);
      } catch (_) { /* ignore */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || "AI is unavailable right now.";
      setMessages((prev) => [...prev, { role: "assistant", text: detail, actions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="help-page" className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <div
            aria-hidden="true"
            style={{
              width: 40, height: 40, borderRadius: 12,
              background: "var(--ds-tile-help-bg)",
              color: "var(--ds-tile-help-ink)",
              display: "grid", placeItems: "center",
            }}
          >
            <HelpCircle size={20} strokeWidth={2.1} />
          </div>
          <div>
            <h1
              data-testid="help-page-title"
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: 28,
                color: "var(--ds-ink)",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
              }}
            >
              AI Navigator
            </h1>
            <p style={{ fontSize: 12.5, color: "var(--brand-muted)", marginTop: 4 }}>
              Ask me how any part of SnapOps works. I&apos;ll link you to the exact screen.
              {quota?.remaining ? (
                <span data-testid="help-page-quota" className="ml-2 opacity-70">
                  · {quota.remaining.navigator} left today
                </span>
              ) : null}
            </p>
          </div>
        </div>

        {!enabled ? (
          // Free-plan gate — friendly upgrade nudge
          <div
            data-testid="help-page-gated"
            className="mt-8 p-6"
            style={{
              background: "linear-gradient(135deg, #FFFDFA 0%, #F6EFDF 100%)",
              border: "1px solid rgba(176,141,85,0.28)",
              borderRadius: 16,
            }}
          >
            <div className="flex items-start gap-3">
              <Sparkles size={18} color="#95643C" />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ds-ink)", marginBottom: 4 }}>
                  Upgrade to unlock the AI Navigator
                </div>
                <p style={{ fontSize: 13, color: "var(--brand-muted)", lineHeight: 1.55 }}>
                  The Navigator is available on Studio, Pro, Trial, and Grandfathered
                  plans. Upgrade to ask questions about the app and get contextual
                  in-app links.
                </p>
                <button
                  data-testid="help-page-upgrade-cta"
                  onClick={() => navigate("/settings")}
                  className="mt-4"
                  style={{
                    background: "var(--ds-ink)",
                    color: "#F7F3EC",
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    cursor: "pointer",
                  }}
                >
                  View plans
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mt-6 flex flex-col"
            style={{
              background: "#FFFDFA",
              border: "1px solid rgba(34,56,44,0.10)",
              borderRadius: 18,
              overflow: "hidden",
              minHeight: "min(720px, calc(100vh - 260px))",
            }}
          >
            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="help-page-messages"
              className="flex-1 overflow-y-auto px-5 py-5 space-y-3"
              style={{ background: "#F9F5EC" }}
            >
              {messages.length === 0 && (
                <div className="space-y-3" data-testid="help-page-empty">
                  <p
                    className="text-sm"
                    style={{ color: "var(--brand-muted)", fontStyle: "italic" }}
                  >
                    Try asking:
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        data-testid={`help-page-suggestion-${s.slice(0, 20)}`}
                        onClick={() => send(s)}
                        className="text-left px-3 py-2.5 rounded-xl text-sm hover:bg-white transition-colors"
                        style={{
                          background: "#FFFDFA",
                          border: "1px solid rgba(34,56,44,0.08)",
                          color: "var(--ds-ink)",
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`help-page-msg-${m.role}`}
                  className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                >
                  <div
                    className="rounded-2xl px-4 py-3 text-sm max-w-[86%]"
                    style={{
                      background: m.role === "user" ? "var(--ds-ink)" : "#FFFDFA",
                      color: m.role === "user" ? "#F7F3EC" : "var(--brand-ink)",
                      border: m.role === "user" ? "none" : "1px solid rgba(34,56,44,0.10)",
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {m.text}
                    {m.role === "assistant" && m.actions?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.actions.map((a) => (
                          <button
                            key={a.route}
                            data-testid={`help-page-action-${a.route}`}
                            onClick={() => navigate(a.route)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                            style={{
                              background: "rgba(176,141,85,0.14)",
                              color: "#95643C",
                              border: "1px solid rgba(176,141,85,0.25)",
                            }}
                          >
                            {a.label} →
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="help-page-loading">
                  <div
                    className="rounded-2xl px-4 py-3 text-sm"
                    style={{
                      background: "#FFFDFA",
                      color: "var(--brand-muted)",
                      border: "1px solid rgba(34,56,44,0.10)",
                    }}
                  >
                    Thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-4 border-t"
              style={{ borderColor: "rgba(34,56,44,0.10)", background: "#FFFDFA" }}
            >
              <div className="flex gap-2">
                <input
                  data-testid="help-page-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="How does SnapOps…"
                  disabled={loading}
                  className="flex-1 px-3 py-2.5 rounded-lg outline-none text-sm"
                  style={{
                    background: "#F7F3EC",
                    border: "1px solid rgba(34,56,44,0.10)",
                    color: "var(--brand-ink)",
                    minHeight: 44,
                  }}
                />
                <button
                  type="submit"
                  data-testid="help-page-send"
                  disabled={!input.trim() || loading}
                  className="px-5 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-40"
                  style={{
                    background: "var(--ds-ink)",
                    color: "#F7F3EC",
                    border: "none",
                    minHeight: 44,
                    minWidth: 74,
                  }}
                >
                  Ask
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}
